import { Fragment, type ReactNode } from 'react';
import { martinaMarkdownInline } from './martinaMarkdownInline';

function renderInlineMarkdown(value: string): ReactNode[] {
  return martinaMarkdownInline(value).map((part, index) => {
    if (part.type === 'strong') return <strong key={index}>{part.text}</strong>;
    if (part.type === 'emphasis') return <em key={index}>{part.text}</em>;
    if (part.type === 'link') return <a key={index} href={part.href}
      {...(part.href.startsWith('https://') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="break-words text-primary underline">{part.text}</a>;
    return <Fragment key={index}>{part.text}</Fragment>;
  });
}

export function normalizeLegacyMartinaAnswer(value: string): string {
  return value
    .replace(/Sono\s+ExAi,\s+la segretaria digitale operativa di Michele\s+[—-]\s+non Martina!/gi, 'Mi chiamo Martina, la segretaria digitale operativa di Michele!')
    .replace(/\bExAi\b/g, 'Martina');
}

export function MartinaMarkdown({ text }: { text: string }) {
  const lines = normalizeLegacyMartinaAnswer(text).split('\n');
  const content: ReactNode[] = [];
  for (let index = 0; index < lines.length; index += 1) {
    const raw = lines[index];
    const line = raw.trim();
    const headers = tableCells(line);
    const separator = tableCells(lines[index + 1] ?? '');
    if (headers && separator?.length === headers.length && separator.every(cell => /^:?-{3,}:?$/.test(cell))) {
      const rows: string[][] = [];
      let next = index + 2;
      for (; next < lines.length; next += 1) {
        const cells = tableCells(lines[next]);
        if (!cells || cells.length !== headers.length) break;
        rows.push(cells);
      }
      content.push(<div key={index} className="max-w-full overflow-x-auto" role="region" aria-label="Tabella nella risposta di Martina" tabIndex={0}>
        <table className="w-full border-collapse text-left text-sm">
          <thead><tr>{headers.map((cell, column) => <th key={column} scope="col" className="border border-border px-2 py-1.5 font-medium">{renderInlineMarkdown(cell)}</th>)}</tr></thead>
          <tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, column) => <td key={column} className="border border-border px-2 py-1.5 align-top font-normal">{renderInlineMarkdown(cell)}</td>)}</tr>)}</tbody>
        </table>
      </div>);
      index = next - 1;
      continue;
    }
    if (!line) { content.push(<div key={index} className="h-1" />); continue; }
    if (/^-{3,}$/.test(line)) { content.push(<hr key={index} className="my-2 border-border" />); continue; }
    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) { content.push(<p key={index} className="mt-2 font-semibold">{renderInlineMarkdown(heading[2])}</p>); continue; }
    const bullet = line.match(/^[-*]\s+(.+)$/);
    if (bullet) { content.push(<div key={index} className="flex gap-2 pl-2"><span aria-hidden>•</span><span className="min-w-0">{renderInlineMarkdown(bullet[1])}</span></div>); continue; }
    const numbered = line.match(/^(\d+)\.\s+(.+)$/);
    if (numbered) { content.push(<div key={index} className="flex gap-2 pl-2"><span>{numbered[1]}.</span><span className="min-w-0">{renderInlineMarkdown(numbered[2])}</span></div>); continue; }
    if (line.startsWith('> ')) { content.push(<blockquote key={index} className="border-l-2 border-primary pl-2 text-muted-foreground">{renderInlineMarkdown(line.slice(2))}</blockquote>); continue; }
    content.push(<p key={index}>{renderInlineMarkdown(raw)}</p>);
  }
  // A long CRM identifier, URL or copied token must wrap inside the message.
  // Otherwise the whole Martina answer becomes thousands of pixels wide on
  // mobile, while the page width still looks normal and hides the text.
  return <div className="min-w-0 max-w-full space-y-1 [overflow-wrap:anywhere] text-foreground">{content}</div>;
}

/** Only Markdown table rows: escaped pipes stay inside a cell. Incomplete
 * tables remain ordinary text rather than losing any part of the answer. */
function tableCells(value: string): string[] | null {
  const line = value.trim();
  const cells: string[] = [];
  let cell = '';
  let hasDelimiter = false;
  for (let index = 0; index < line.length; index += 1) {
    if (line[index] === '\\' && line[index + 1] === '|') { cell += '|'; index += 1; }
    else if (line[index] === '|') { cells.push(cell.trim()); cell = ''; hasDelimiter = true; }
    else cell += line[index];
  }
  if (!hasDelimiter) return null;
  cells.push(cell.trim());
  if (line.startsWith('|')) cells.shift();
  if (line.endsWith('|') && !line.endsWith('\\|')) cells.pop();
  return cells.length >= 2 ? cells : null;
}
